import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { EmptyState } from '@/components/campus';
import {
  Crown,
  Building2,
  Search,
  Loader2,
  UserPlus,
  Mail,
  AlertTriangle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;


export const Presidents = () => {
  const { session } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [inviteMode, setInviteMode] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');

  // Fetch presidents list
  const fetchPresidents = async () => {
    if (!session?.access_token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/users/presidents`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrganizations(data.organizations || []);
      }
    } catch (error) {
      console.error('Failed to fetch presidents:', error);
      toast.error('Failed to load presidents list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPresidents();
  }, [session?.access_token]);

  // Search users for assignment
  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(
        `${API_URL}/api/platform/organizations/${selectedOrg?.organization_id}/search-users?query=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.users || []);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  // Assign president
  const handleAssignPresident = async (userId) => {
    setAssigning(true);
    try {
      const res = await fetch(
        `${API_URL}/api/platform/organizations/${selectedOrg?.organization_id}/assign-president`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ user_id: userId, confirm_replace: true }),
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        setAssignDialogOpen(false);
        fetchPresidents();
      } else if (data.requires_confirmation) {
        // Show confirmation
        if (window.confirm(`${data.existing_president?.display_name || data.existing_president?.email} is currently President. Replace them?`)) {
          // Retry with confirm
          const retryRes = await fetch(
            `${API_URL}/api/platform/organizations/${selectedOrg?.organization_id}/assign-president`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({ user_id: userId, confirm_replace: true }),
            }
          );
          const retryData = await retryRes.json();
          if (retryRes.ok) {
            toast.success(retryData.message);
            setAssignDialogOpen(false);
            fetchPresidents();
          }
        }
      } else {
        toast.error(data.detail || data.message || 'Failed to assign president');
      }
    } catch (error) {
      toast.error('Failed to assign president');
    } finally {
      setAssigning(false);
    }
  };

  // Invite new president
  const handleInvitePresident = async () => {
    if (!inviteEmail) {
      toast.error('Email is required');
      return;
    }
    setAssigning(true);
    try {
      const res = await fetch(
        `${API_URL}/api/platform/organizations/${selectedOrg?.organization_id}/invite-president`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ email: inviteEmail, name: inviteName }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Invitation sent');
        setAssignDialogOpen(false);
        setInviteEmail('');
        setInviteName('');
        setInviteMode(false);
      } else {
        toast.error(data.detail || 'Failed to send invitation');
      }
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setAssigning(false);
    }
  };

  // Filter organizations
  const filteredOrgs = organizations.filter(org =>
    org.organization_name?.toLowerCase().includes(search.toLowerCase()) ||
    org.president?.display_name?.toLowerCase().includes(search.toLowerCase()) ||
    org.president?.email?.toLowerCase().includes(search.toLowerCase())
  );

  const withPresident = filteredOrgs.filter(o => o.has_president);
  const withoutPresident = filteredOrgs.filter(o => !o.has_president);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-electric-ice-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-presidents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-cloud-white flex items-center gap-2">
            <Crown className="w-6 h-6 text-amber-400" />
            Organization Presidents
          </h1>
          <p className="text-soft-silver text-sm mt-1">
            {organizations.filter(o => o.has_president).length} of {organizations.length} organizations have a President assigned
          </p>
        </div>
        
        {/* Search */}
        <div className="relative w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
          <Input
            placeholder="Search organizations or presidents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-obsidian border-white/[0.08]"
          />
        </div>
      </div>

      {/* Organizations without President */}
      {withoutPresident.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <h3 className="text-amber-400 font-semibold flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4" />
            Organizations without a President ({withoutPresident.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {withoutPresident.map((org) => (
              <div key={org.organization_id} className="bg-obsidian/50 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="text-cloud-white font-medium">{org.organization_name}</p>
                  <p className="text-xs text-muted-text">{org.campus_name || org.organization_type}</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setSelectedOrg(org);
                    setAssignDialogOpen(true);
                  }}
                  className="bg-amber-500 text-black hover:bg-amber-600"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Assign
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Organizations with President */}
      <div className="bg-obsidian/50 border border-white/[0.08] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.08]">
              <th className="text-left text-xs font-medium text-muted-text uppercase tracking-wider px-4 py-3">Organization</th>
              <th className="text-left text-xs font-medium text-muted-text uppercase tracking-wider px-4 py-3">Type</th>
              <th className="text-left text-xs font-medium text-muted-text uppercase tracking-wider px-4 py-3">Campus</th>
              <th className="text-left text-xs font-medium text-muted-text uppercase tracking-wider px-4 py-3">President</th>
              <th className="text-left text-xs font-medium text-muted-text uppercase tracking-wider px-4 py-3">Status</th>
              <th className="text-right text-xs font-medium text-muted-text uppercase tracking-wider px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.06]">
            {withPresident.map((org) => (
              <tr key={org.organization_id} className="hover:bg-white/[0.02] transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-electric-ice-blue" />
                    <span className="text-cloud-white font-medium">{org.organization_name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-soft-silver capitalize">{org.organization_type?.replace('_', ' ')}</td>
                <td className="px-4 py-3 text-soft-silver">{org.campus_name || '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Crown className="w-4 h-4 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-cloud-white font-medium">{org.president?.display_name || 'Unknown'}</p>
                      <p className="text-xs text-muted-text">{org.president?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {org.is_active ? (
                    <Badge className="bg-fresh-mint/20 text-fresh-mint border-fresh-mint/30">Active</Badge>
                  ) : (
                    <Badge className="bg-gray-500/20 text-gray-400">Inactive</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedOrg(org);
                      setAssignDialogOpen(true);
                    }}
                    className="border-white/[0.08] text-soft-silver hover:text-cloud-white"
                  >
                    Change
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {withPresident.length === 0 && (
          <div className="p-8">
            <EmptyState
              icon="crown"
              title="No organizations with presidents"
              description="Assign presidents to organizations above"
            />
          </div>
        )}
      </div>

      {/* Assign President Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={(open) => {
        setAssignDialogOpen(open);
        if (!open) {
          setUserSearch('');
          setSearchResults([]);
          setInviteMode(false);
          setInviteEmail('');
          setInviteName('');
        }
      }}>
        <DialogContent className="bg-graphite border-white/[0.08] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-cloud-white">
              {inviteMode ? 'Invite New President' : 'Assign President'}
            </DialogTitle>
            <DialogDescription className="text-soft-silver">
              {selectedOrg?.organization_name}
              {selectedOrg?.has_president && (
                <span className="block text-amber-400 text-xs mt-1">
                  Current President: {selectedOrg.president?.display_name || selectedOrg.president?.email}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {inviteMode ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-soft-silver mb-1 block">Email *</label>
                <Input
                  type="email"
                  placeholder="president@university.edu"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="bg-obsidian border-white/[0.08]"
                />
              </div>
              <div>
                <label className="text-sm text-soft-silver mb-1 block">Name (optional)</label>
                <Input
                  placeholder="John Smith"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="bg-obsidian border-white/[0.08]"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteMode(false)}>
                  Back to Search
                </Button>
                <Button onClick={handleInvitePresident} disabled={assigning || !inviteEmail}>
                  {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send Invitation
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
                <Input
                  placeholder="Search users by name or email..."
                  value={userSearch}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    searchUsers(e.target.value);
                  }}
                  className="pl-10 bg-obsidian border-white/[0.08]"
                />
              </div>

              <div className="max-h-60 overflow-y-auto space-y-2">
                {searching ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-electric-ice-blue" />
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-obsidian/50 rounded-lg border border-white/[0.08] hover:border-electric-ice-blue/30 cursor-pointer transition-colors"
                      onClick={() => handleAssignPresident(user.id)}
                    >
                      <div>
                        <p className="text-cloud-white font-medium">{user.display_name || user.email}</p>
                        <p className="text-xs text-muted-text">{user.email}</p>
                      </div>
                      {user.is_member && (
                        <Badge className="bg-white/10 text-soft-silver text-xs">
                          {user.current_role === 'owner' ? 'President' : user.current_role?.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                  ))
                ) : userSearch.length >= 2 ? (
                  <p className="text-center text-muted-text py-4">No users found</p>
                ) : (
                  <p className="text-center text-muted-text py-4">Type at least 2 characters to search</p>
                )}
              </div>

              <div className="border-t border-white/[0.08] pt-4">
                <Button
                  variant="outline"
                  onClick={() => setInviteMode(true)}
                  className="w-full border-white/[0.08]"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Invite New User as President
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Presidents;
